<template>
    <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-3 sm:p-6"
        @click.self="close"
    >
        <div
            ref="dialog"
            class="relative flex h-full max-h-[780px] w-full max-w-[960px] flex-col overflow-hidden rounded-[8px] border border-[#ECE8E1] bg-white dark:border-white/10 dark:bg-dark-secondary"
            role="dialog"
            aria-modal="true"
            :aria-label="`${name} in 3D`"
            tabindex="-1"
        >
            <div class="flex flex-none items-start justify-between gap-4 border-b border-bdr-clr dark:border-bdr-clr-drk px-4 py-4 sm:px-6">
                <div class="min-w-0">
                    <h5 class="truncate text-base sm:text-lg font-medium leading-none text-title dark:text-white">{{ name }}</h5>
                    <span class="mt-2 block text-xs sm:text-sm leading-none text-paragraph dark:text-white-light">
                        Drag to spin, scroll or pinch to zoom
                    </span>
                </div>
                <button
                    ref="closeButton"
                    type="button"
                    aria-label="Close 3D viewer"
                    class="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#F6F2EC] text-title duration-300 hover:bg-primary hover:text-white dark:bg-white/10 dark:text-white dark:hover:bg-primary"
                    @click="close"
                >
                    <i class="mdi mdi-close text-[20px]"></i>
                </button>
            </div>

            <div class="relative min-h-0 flex-1 bg-[#F6F2EC] dark:bg-title">
                <!-- touch-action="none" hands every drag to the viewer. "pan-y" would give
                     vertical drags back to the page to scroll with, and inside a modal that
                     has locked the body there is nothing to scroll — so on a phone, tilting
                     the piece up and down would simply stop working. -->
                <model-viewer
                    v-if="libraryReady"
                    ref="viewer"
                    class="ar-stage"
                    :src="glbUrl"
                    :ios-src="usdzUrl || undefined"
                    :alt="name"
                    :poster="poster || undefined"
                    camera-controls
                    touch-action="none"
                    shadow-intensity="1"
                    :exposure="1"
                    :ar="true"
                    :ar-modes="arModes"
                    :ar-placement="placement"
                    :ar-scale="scale"
                    @load="onModelLoad"
                    @error="onModelError"
                    @ar-status="onArStatus"
                    @ar-tracking="onArTracking"
                >
                    <!-- Not the deprecated Vue 2 slot syntax the rule guards against: model-viewer
                         is a real web component and this is the shadow-DOM slot it projects the AR
                         button into, which is also how model-viewer wires up the tap handler. -->
                    <!-- eslint-disable vue/no-deprecated-slot-attribute -->
                    <button
                        v-if="canActivateAr"
                        slot="ar-button"
                        type="button"
                        class="ar-cta btn btn-solid btn-sm"
                        data-text="View in your room"
                    >
                        <span>View in your room</span>
                    </button>
                    <!-- eslint-enable vue/no-deprecated-slot-attribute -->
                </model-viewer>

                <!-- The poster keeps painting underneath, so both overlays carry their own ground
                     or the message reads on top of the product photo. -->
                <div
                    v-if="failure"
                    class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#F6F2EC]/95 px-6 text-center backdrop-blur-[2px] dark:bg-title/95"
                >
                    <i class="mdi mdi-alert-circle-outline text-[32px] text-[#E13939]"></i>
                    <p class="max-w-[380px] text-base leading-relaxed text-paragraph dark:text-white-light">{{ failure }}</p>
                    <button type="button" class="btn btn-outline btn-sm" data-text="Close" @click="close">
                        <span>Close</span>
                    </button>
                </div>
                <div
                    v-else-if="!modelReady"
                    class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F6F2EC]/95 px-6 text-center backdrop-blur-[2px] dark:bg-title/95"
                >
                    <i class="mdi mdi-loading mdi-spin text-[32px] text-primary"></i>
                    <p class="text-base leading-none text-paragraph dark:text-white-light">Loading 3D model...</p>
                </div>
            </div>

            <!-- Diagnostics. There is no console to open on a phone, so when ?ardebug=1 is on
                 the URL the AR session's own events are printed here instead, with a copy
                 button so the trace can be sent to someone who can read it. -->
            <div v-if="arDebug" class="flex-none border-t border-bdr-clr dark:border-bdr-clr-drk bg-[#101014] px-4 py-3 sm:px-6">
                <div class="flex items-center justify-between gap-3">
                    <span class="font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">AR diagnostics</span>
                    <button type="button" class="font-mono text-[11px] text-white/70 underline" @click="copyLog">
                        {{ copied ? 'copied' : 'copy' }}
                    </button>
                </div>
                <div class="mt-2 max-h-[140px] overflow-y-auto">
                    <p
                        v-for="(line, i) in arLog"
                        :key="i"
                        class="font-mono text-[11px] leading-[1.5] text-white/85"
                        :class="line.level === 'bad' ? 'text-[#FF8A8A]' : (line.level === 'good' ? 'text-[#8FE0A5]' : '')"
                    >{{ line.t }} {{ line.msg }}</p>
                </div>
            </div>

            <div v-if="modelReady && !failure" class="flex-none border-t border-bdr-clr dark:border-bdr-clr-drk px-4 py-4 sm:px-6">
                <!-- A phone finds a surface by tracking visual detail on it. A bare, evenly
                     coloured floor gives it nothing to lock onto, so the piece drifts or
                     vanishes. Say so plainly, because it reads as a broken app otherwise. -->
                <p v-if="lostTracking" class="flex items-start gap-2 text-sm leading-relaxed text-paragraph dark:text-white-light">
                    <i class="mdi mdi-alert-outline mt-[2px] text-[18px] text-[#C9891F]"></i>
                    <span>
                        Your phone lost track of the floor. Plain, single-colour floors give the camera
                        nothing to hold on to — move the phone slowly across the floor first, add more
                        light, or aim where there is a rug, a tile edge or some grain.
                    </span>
                </p>
                <p v-else-if="canActivateAr" class="flex items-start gap-2 text-sm leading-relaxed text-paragraph dark:text-white-light">
                    <i class="mdi mdi-cube-scan mt-[2px] text-[18px] text-primary"></i>
                    <span>Tap &ldquo;View in your room&rdquo;, then sweep your camera slowly across the {{ placement === 'wall' ? 'wall' : 'floor' }} until the piece appears.</span>
                </p>
                <p v-else class="flex items-start gap-2 text-sm leading-relaxed text-paragraph dark:text-white-light">
                    <i class="mdi mdi-cellphone mt-[2px] text-[18px] text-primary"></i>
                    <span>{{ noArMessage }}</span>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

    const props = defineProps({
        glbUrl: { type: String, default: '' },
        usdzUrl: { type: String, default: '' },
        poster: { type: String, default: '' },
        name: { type: String, default: '' },
        placement: { type: String, default: 'floor' },
        scale: { type: String, default: 'fixed' },
    });

    const emit = defineEmits(['close']);

    const libraryReady = ref(false);
    const modelReady = ref(false);
    const canActivateAr = ref(false);
    const failure = ref('');
    const viewer = ref(null);
    const dialog = ref(null);
    const closeButton = ref(null);
    const lostTracking = ref(false);
    const arLog = ref([]);
    const copied = ref(false);

    // Enabled with ?ardebug=1, because a phone has no console to open.
    const arDebug = ref(false);
    const forcedMode = ref('');

    /**
     * Scene Viewer before WebXR, deliberately.
     *
     * With "webxr" first, Android stays in the browser and model-viewer does its own
     * plane-finding, which gives up quickly on a plain floor and takes the model with
     * it. Scene Viewer is Google's native viewer: it uses the same ARCore tracking the
     * camera app does, and it coaches the user through scanning a surface instead of
     * silently showing nothing. iOS is unaffected — it always goes to Quick Look.
     *
     * The trade-off is that Scene Viewer runs outside the browser and so cannot report
     * tracking back to this page. ?armode=webxr forces the in-browser path when the
     * detailed ar-tracking log is what you are after.
     */
    const arModes = computed(() => (forcedMode.value === 'webxr'
        ? 'webxr scene-viewer quick-look'
        : 'scene-viewer webxr quick-look'));

    function logLine(msg, level = '') {
        const now = new Date();
        const t = `${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`;
        arLog.value.push({ t, msg, level });
        if (arLog.value.length > 60) arLog.value.shift();
        // Always emitted, so a desktop console shows the same trace as the phone panel.
        console.log(`[AR ${t}] ${msg}`);
    }

    async function copyLog() {
        const text = arLog.value.map((l) => `${l.t} ${l.msg}`).join('\n');
        try {
            await navigator.clipboard.writeText(text);
            copied.value = true;
            window.setTimeout(() => { copied.value = false; }, 1800);
        } catch (e) {
            logLine(`could not copy: ${e}`, 'bad');
        }
    }

    // model-viewer reports the AR session's own lifecycle here. Without listening, a
    // session that starts and then fails looks identical to one that was never tapped.
    function onArStatus(event) {
        const status = event?.detail?.status || 'unknown';
        const level = status === 'failed' ? 'bad' : (status === 'object-placed' ? 'good' : '');
        logLine(`ar-status: ${status}`, level);

        if (status === 'failed') {
            logLine('the AR session could not start — see the reason above, if any', 'bad');
        }
        if (status === 'session-started') {
            lostTracking.value = false;
        }
    }

    function onArTracking(event) {
        const status = event?.detail?.status || 'unknown';
        logLine(`ar-tracking: ${status}`, status === 'tracking' ? 'good' : 'bad');
        lostTracking.value = status !== 'tracking';
    }

    let arProbeTimer = null;
    let previousBodyOverflow = '';
    let previouslyFocused = null;

    const noArMessage = computed(() => {
        // The camera APIs are gated on a secure context, so an http origin is the one
        // failure we can name precisely instead of blaming the device.
        if (typeof window !== 'undefined' && window.isSecureContext === false) {
            return 'Placing this piece in your room needs a secure (https) connection.';
        }

        return 'Spin it here to see every angle. To place it in your room at true size, open this page on your phone.';
    });

    function close() {
        emit('close');
    }

    function onKeydown(event) {
        if (event.key === 'Escape') {
            close();
            return;
        }

        if (event.key === 'Tab') {
            trapTab(event);
        }
    }

    // aria-modal tells a screen reader everything behind this dialog is hidden, so
    // letting Tab walk out there would march a keyboard user through the product
    // page, the share links and the whole footer — every stop invisible under the
    // backdrop and unannounced. Keep the cycle inside.
    function trapTab(event) {
        const root = dialog.value;
        if (!root) {
            return;
        }

        const stops = [...root.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
            .filter((element) => element.offsetParent !== null || element === document.activeElement);

        if (!stops.length) {
            return;
        }

        const first = stops[0];
        const last = stops[stops.length - 1];
        const active = document.activeElement;
        const inside = root.contains(active);

        if (event.shiftKey && (!inside || active === first)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (!inside || active === last)) {
            event.preventDefault();
            first.focus();
        }
    }

    // model-viewer resolves AR support asynchronously (the WebXR probe is a promise), so the
    // flag can still read false when `load` fires. Re-read it a few times before we tell the
    // customer their device cannot do this — a dead AR button is worse than a late one.
    function probeArSupport(attemptsLeft = 8) {
        const element = viewer.value;

        // Guards the window where the element exists but the custom element has not upgraded.
        if (element && element.canActivateAR === true) {
            canActivateAr.value = true;
            logLine('device can start AR', 'good');
            return;
        }

        if (attemptsLeft <= 0) {
            logLine(`device reports no AR after ${8 * 250}ms — showing the 3D-only view`, 'bad');
            return;
        }

        arProbeTimer = window.setTimeout(() => probeArSupport(attemptsLeft - 1), 250);
    }

    function onModelLoad() {
        modelReady.value = true;

        // Real-world size is the single most common thing wrong with a supplied model,
        // and it is invisible until someone puts it next to a real sofa. Print it.
        const element = viewer.value;
        try {
            const d = element?.getDimensions?.();
            if (d) {
                logLine(`model loaded: ${d.x.toFixed(2)} x ${d.y.toFixed(2)} x ${d.z.toFixed(2)} m`, 'good');
                if (d.x > 6 || d.y > 4 || d.z > 6) {
                    logLine('that is very large — the model may not be built at 1 unit = 1 metre', 'bad');
                }
                if (d.x < 0.15 && d.y < 0.15 && d.z < 0.15) {
                    logLine('that is tiny — the model may be in centimetres rather than metres', 'bad');
                }
            }
        } catch (e) {
            logLine(`could not read model dimensions: ${e}`, 'bad');
        }

        probeArSupport();
    }

    function onModelError(event) {
        failure.value = 'We could not load the 3D model for this piece. Please try again in a moment.';
        logLine(`model failed to load: ${event?.detail?.type || 'unknown error'}`, 'bad');
    }

    onMounted(async () => {
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeydown);

        try {
            const query = new URLSearchParams(window.location.search);
            arDebug.value = query.has('ardebug');
            forcedMode.value = query.get('armode') || '';
        } catch (e) { /* a malformed query string is not worth failing over */ }

        // The environment decides whether AR is even possible, so record it before
        // anything else — half of "it does not work" turns out to be plain http.
        logLine(`secure context: ${window.isSecureContext}`, window.isSecureContext ? 'good' : 'bad');
        logLine(`webxr available: ${!!navigator.xr}`);
        logLine(`ar modes requested: ${arModes.value}${forcedMode.value ? ' (forced)' : ''}`);
        logLine(`glb: ${props.glbUrl ? 'yes' : 'MISSING'}  usdz: ${props.usdzUrl ? 'yes' : 'none (no iPhone AR)'}`,
            props.glbUrl ? '' : 'bad');
        logLine(`placement: ${props.placement}  scale: ${props.scale}`);

        // Whoever opened this got here from the "View in your room" button, which is
        // now buried under the backdrop; move them into the dialog and remember where
        // to put them back.
        previouslyFocused = document.activeElement;
        await nextTick();
        (closeButton.value || dialog.value)?.focus();

        try {
            // Loaded here rather than at the top of the file so the viewer ships as its own
            // webpack chunk — the shop stays light for everyone who never opens this modal.
            await import('@google/model-viewer');
            libraryReady.value = true;
        } catch (importError) {
            failure.value = 'The 3D viewer could not be loaded. Check your connection and try again.';
            logLine(`3D library failed to load: ${importError}`, 'bad');
        }
    });

    onBeforeUnmount(() => {
        window.clearTimeout(arProbeTimer);
        window.removeEventListener('keydown', onKeydown);
        document.body.style.overflow = previousBodyOverflow;

        // Without this, focus falls to <body> and the next Tab restarts at the top
        // of the page rather than at the button they just came from.
        if (previouslyFocused && document.contains(previouslyFocused)) {
            previouslyFocused.focus();
        }
    });
</script>

<style scoped>
    .ar-stage {
        width: 100%;
        height: 100%;
        --poster-color: transparent;
        --progress-bar-color: #bb976d;
    }

    /* .btn is position: relative, but this one has to float over the model. */
    .ar-cta {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
    }
</style>
